<!--
  PREVENTIVO › SPOSTAMENTO INTERNO.

  Partenza e arrivo: una sede del partner oppure un indirizzo scritto a mano.
  Il server calcola la distanza su strada (`POST /journey/leg-distance`, la
  stessa della creazione di uno spostamento) e il prezzo con la tariffa del
  PARTNER (`POST /tariff-band/quote` con `share: true`), a 1 e a 2 operatori:
  è quanto paga il cliente. Dipendente e venditore non possono chiedere la
  tariffa Simba. Importi senza IVA.
-->
<template>
  <div class="psi">
    <section class="psi__card">
      <header class="psi__head">
        <h2 class="psi__title">
          Percorso
        </h2>
        <p class="psi__note">
          Il prezzo dipende dalla distanza su strada fra partenza e arrivo.
        </p>
      </header>

      <div class="psi__body">
        <div
          v-for="e in estremi"
          :key="e.chiave"
          class="psi__estremo"
        >
          <div class="psi__label">
            {{ e.titolo }}
          </div>
          <div
            class="psi__seg"
            role="radiogroup"
            :aria-label="`${e.titolo}: sede o indirizzo`"
          >
            <button
              type="button"
              role="radio"
              :aria-checked="e.stato.modo === 'sede'"
              :class="{ 'is-on': e.stato.modo === 'sede' }"
              :disabled="!sedi.length"
              @click="e.stato.modo = 'sede'"
            >
              Una sede
            </button>
            <button
              type="button"
              role="radio"
              :aria-checked="e.stato.modo === 'libera'"
              :class="{ 'is-on': e.stato.modo === 'libera' }"
              @click="e.stato.modo = 'libera'"
            >
              Scrivi l'indirizzo
            </button>
          </div>

          <el-select
            v-if="e.stato.modo === 'sede'"
            v-model="e.stato.sedeId"
            placeholder="Scegli la sede"
            class="psi__sede"
            :aria-label="`${e.titolo}: sede`"
          >
            <el-option
              v-for="s in sedi"
              :key="s.id"
              :label="etichettaSede(s)"
              :value="s.id"
            />
          </el-select>
          <div
            v-else
            class="psi__grid"
          >
            <div class="psi__f psi__f--2">
              <label :for="`psi-${e.chiave}-via`">Via / Piazza</label>
              <el-input
                :id="`psi-${e.chiave}-via`"
                v-model="e.stato.indirizzo.address"
              />
            </div>
            <div class="psi__f">
              <label :for="`psi-${e.chiave}-civico`">Civico</label>
              <el-input
                :id="`psi-${e.chiave}-civico`"
                v-model="e.stato.indirizzo.street_number"
              />
            </div>
            <div class="psi__f">
              <label :for="`psi-${e.chiave}-cap`">CAP</label>
              <el-input
                :id="`psi-${e.chiave}-cap`"
                v-model="e.stato.indirizzo.cap"
                maxlength="5"
              />
            </div>
            <div class="psi__f">
              <label :for="`psi-${e.chiave}-citta`">Città</label>
              <el-input
                :id="`psi-${e.chiave}-citta`"
                v-model="e.stato.indirizzo.city"
              />
            </div>
            <div class="psi__f">
              <label :for="`psi-${e.chiave}-provincia`">Provincia</label>
              <el-input
                :id="`psi-${e.chiave}-provincia`"
                v-model="e.stato.indirizzo.province"
                maxlength="2"
                placeholder="es. BG"
              />
            </div>
          </div>
        </div>

        <div class="psi__actions">
          <span
            v-if="tentato && problema"
            class="psi__err"
            role="alert"
          >{{ problema }}</span>
          <span class="psi__spacer" />
          <el-button
            type="primary"
            :loading="calcolo"
            @click="calcola"
          >
            Calcola preventivo
          </el-button>
        </div>
      </div>
    </section>

    <section
      v-if="risultato"
      class="psi__card"
      aria-live="polite"
    >
      <header class="psi__head">
        <h2 class="psi__title">
          Preventivo
        </h2>
        <p class="psi__note">
          {{ numero(risultato.km) }} km su strada · circa {{ durata(risultato.min) }}
        </p>
      </header>

      <div class="psi__body">
        <div class="psi__scroll">
          <table class="psi__tab">
            <thead>
              <tr>
                <th>Operatori</th>
                <th class="num">
                  Al cliente, a corsa
                </th>
                <th>Tariffa</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="r in risultato.righe"
                :key="r.operatori"
              >
                <td class="psi__opt">
                  {{ r.operatori === 1 ? 'Spostamento a catena' : 'Spostamento singolo' }}
                </td>
                <td class="num psi__strong">
                  {{ r.prezzo === null ? '—' : `${euro(r.prezzo)} + IVA` }}
                </td>
                <td class="psi__muted">
                  {{ r.nota }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p class="psi__help">
          Una corsa, senza IVA, con le tariffe del partner in vigore oggi.
          <template v-if="risultato.righe.some((r) => r.prezzo === null)">
            Dove il prezzo manca, la tariffa del partner per questa distanza non è
            impostata: la imposta l'amministratore nella pagina Tariffe.
          </template>
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import { computed, onMounted, reactive, ref } from 'vue';
import { ElMessage } from 'element-plus';
import bookingAPI from '../services/bookingAPI.js';
import journeyAPI from '../services/journeyAPI.js';
import { messageForError } from '../services/httpErrors';

const vuoto = () => ({ address: '', street_number: '', cap: '', city: '', province: '' });
const pulito = (v) => String(v ?? '').trim();
const euroFmt = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' });
const coordinata = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? undefined : Number(v));

export default {
  name: 'PreventivoSpostamentoInterno',

  setup() {
    const sedi = ref([]);
    const partenza = reactive({ modo: 'sede', sedeId: '', indirizzo: vuoto() });
    const arrivo = reactive({ modo: 'libera', sedeId: '', indirizzo: vuoto() });
    const estremi = [
      { chiave: 'partenza', titolo: 'Partenza', stato: partenza },
      { chiave: 'arrivo', titolo: 'Arrivo', stato: arrivo },
    ];
    const tentato = ref(false);
    const calcolo = ref(false);
    const risultato = ref(null);

    onMounted(async () => {
      try {
        // Tutte le sedi del partner: qui una sede è un punto di partenza o di
        // arrivo, non il posto dove lavora chi chiede il preventivo.
        const res = await bookingAPI.getHeadquartersOfPartner();
        sedi.value = res?.data?.data ?? res?.data ?? [];
        if (sedi.value.length === 1) partenza.sedeId = sedi.value[0].id;
      } catch (e) {
        console.warn('Sedi non lette:', e?.message);
      }
      if (!sedi.value.length) {
        partenza.modo = 'libera';
        arrivo.modo = 'libera';
      }
    });

    const etichettaSede = (s) => [s.name, [s.address, s.street_number].filter(Boolean).join(' '), s.city]
      .filter(Boolean)
      .join(' · ');
    const completo = (a) => pulito(a.address) && pulito(a.city);

    const problema = computed(() => {
      for (const e of estremi) {
        const st = e.stato;
        if (st.modo === 'sede' && !st.sedeId) return `${e.titolo}: scegli la sede`;
        if (st.modo === 'libera' && !completo(st.indirizzo)) return `${e.titolo}: servono almeno via e città`;
      }
      if (partenza.modo === 'sede' && arrivo.modo === 'sede' && partenza.sedeId === arrivo.sedeId) {
        return 'Partenza e arrivo sono la stessa sede';
      }
      return '';
    });

    // Punto nel formato di `/journey/leg-distance` (PointLegDto). Con una sede
    // si manda anche il suo id: il backend usa la cache delle distanze.
    const punto = (st) => {
      if (st.modo === 'sede') {
        const s = sedi.value.find((x) => x.id === st.sedeId) || {};
        return {
          headquarter_id: s.id,
          address: pulito(s.address),
          street_number: pulito(s.street_number),
          cap: pulito(s.cap),
          city: pulito(s.city),
          province: pulito(s.province).toUpperCase(),
          latitude: coordinata(s.latitude),
          longitude: coordinata(s.longitude),
        };
      }
      const a = st.indirizzo;
      return {
        address: pulito(a.address),
        street_number: pulito(a.street_number),
        cap: pulito(a.cap),
        city: pulito(a.city),
        province: pulito(a.province).toUpperCase(),
      };
    };

    const NOTE = {
      partner: 'Tariffa del partner',
      global: 'Tariffa generale del partner',
      none: 'Non impostata',
    };

    const calcola = async () => {
      tentato.value = true;
      if (problema.value) return;
      calcolo.value = true;
      try {
        const { data: tratta } = await journeyAPI.previewLeg(punto(partenza), punto(arrivo));
        const km = Number(tratta.km) || 0;
        const oggi = new Date().toISOString().slice(0, 10);
        const prezzi = await Promise.all([1, 2].map((op) => journeyAPI.quotePartner(km, oggi, op)));
        risultato.value = {
          km,
          min: Number(tratta.min) || 0,
          righe: prezzi.map((res, i) => ({
            operatori: i + 1,
            prezzo: res.data?.price ?? null,
            nota: NOTE[res.data?.scope] || NOTE.none,
          })),
        };
      } catch (e) {
        console.error('Preventivo spostamento non calcolato:', e);
        risultato.value = null;
        ElMessage({ type: 'error', showClose: true, message: messageForError(e) });
      } finally {
        calcolo.value = false;
      }
    };

    const numero = (v) => (Math.round(Number(v) * 10) / 10).toLocaleString('it-IT');
    const durata = (min) => {
      const m = Math.round(Number(min) || 0);
      if (m < 60) return `${m} min`;
      return `${Math.floor(m / 60)} h ${m % 60} min`;
    };
    const euro = (v) => euroFmt.format(v);

    return {
      sedi, estremi, tentato, calcolo, risultato, problema, etichettaSede, calcola, numero, durata, euro,
    };
  },
};
</script>

<style scoped>
.psi { display: flex; flex-direction: column; gap: 16px; text-align: left; }
.psi__card { background: #fff; border: 1px solid var(--color-border); border-radius: 6px; box-shadow: var(--el-box-shadow-lighter); overflow: hidden; }
.psi__head { padding: 13px 18px; border-bottom: 1px solid var(--color-border); }
.psi__title { margin: 0; font-size: 14.5px; font-weight: 600; }
.psi__note { margin: 3px 0 0; font-size: 12.5px; color: var(--color-text-secondary); }
.psi__body { padding: 16px 18px; display: flex; flex-direction: column; gap: 16px; }
.psi__estremo { display: flex; flex-direction: column; gap: 10px; }
.psi__estremo + .psi__estremo { padding-top: 16px; border-top: 1px solid var(--color-bg-tertiary); }
.psi__label {
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-secondary);
}
.psi__grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 12px 14px; }
.psi__f { display: flex; flex-direction: column; min-width: 0; }
.psi__f--2 { grid-column: span 2; }
.psi__f label { margin-bottom: 4px; font-size: 12px; font-weight: 600; color: var(--color-text-secondary); }
.psi__sede { max-width: 480px; }

.psi__seg { display: inline-flex; align-self: flex-start; border: 1px solid #dcdfe6; border-radius: 4px; overflow: hidden; }
.psi__seg button {
  font: inherit; font-size: 13px; font-weight: 600; height: 30px; padding: 0 14px; border: 0;
  background: #fff; color: var(--color-text-secondary); cursor: pointer;
}
.psi__seg button + button { border-left: 1px solid #dcdfe6; }
.psi__seg button.is-on { background: var(--color-primary); color: #fff; }
.psi__seg button:disabled { opacity: 0.5; cursor: default; }
.psi__seg button:focus-visible { outline: 2px solid var(--color-primary); outline-offset: 2px; }

.psi__actions { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
.psi__spacer { flex: 1 1 auto; }
.psi__err { font-size: 12.5px; font-weight: 600; color: var(--state-attn); }
.psi__help { margin: 0; font-size: 12px; line-height: 1.5; color: var(--color-text-muted); }

.psi__scroll { overflow-x: auto; }
.psi__tab { width: 100%; min-width: 420px; border-collapse: collapse; }
.psi__tab th {
  text-align: left; padding: 8px; border-bottom: 1px solid var(--color-border);
  font-size: 11px; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; color: var(--color-text-secondary);
}
.psi__tab td { padding: 10px 8px; border-bottom: 1px solid var(--color-bg-tertiary); font-size: 13.5px; }
.psi__tab tr:last-child td { border-bottom: 0; }
.psi__tab .num { text-align: right; font-variant-numeric: tabular-nums; }
.psi__opt { font-weight: 600; }
.psi__strong { font-weight: 700; }
.psi__muted { color: var(--color-text-muted); }

@media (max-width: 700px) {
  .psi__grid { grid-template-columns: 1fr 1fr; }
}
</style>
